/**
 * VMS internal user-action telemetry — Azure Application Insights
 *
 * All events are sent to the App Insights workspace configured by
 * VITE_AZURE_APP_INSIGHTS_CONNECTION. Event names follow the
 * 'VMS <Verb> <Object>' convention (Title Case, space-separated) so
 * the workspace can be queried with:
 *   customEvents | where name startswith "VMS "
 *
 * The one pre-existing live event ('VMS Create Poi') retains its
 * original casing (see design Decision 3).
 *
 * This module is a no-op in dev builds because appInsights.loadAppInsights()
 * is only called in production (src/index.jsx). The optional-chain in
 * track() handles the null case safely.
 */

import appInsights from '../utils/appInsights.js';

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

function track(name, properties) {
    try {
        appInsights?.trackEvent({ name, properties });
    } catch {
        // Swallow — telemetry must never break the app
    }
}

// ---------------------------------------------------------------------------
// POI recording
// ---------------------------------------------------------------------------

export function recordEntitiesUpdate(venueId, updates, isBulkUpdate) {
    track('VMS Update Entities', { venueId, updates, isBulkUpdate });
}

export function recordSavePoi(venueId, updatedEntities) {
    track('VMS Save Poi', { venueId, updatedEntities });
}

export function recordAddPoi(venueId) {
    track('VMS Add Poi', { venueId });
}

export function recordShowRemoveConfirmation(venueId) {
    track('VMS Show Remove Confirmation', { venueId });
}

export function recordHideRemoveConfirmation(venueId, userResponse, poiId) {
    track('VMS Hide Remove Confirmation', { venueId, userResponse, poiId });
}

export function recordPoiClick(venueId, id, query) {
    track('VMS Click Poi', { venueId, id, query });
}

export function recordCreatePoi(venueId, poiData) {
    // Preserves the exact event name already in the App Insights workspace
    // (see design Decision 3 — do not normalise to 'VMS Create POI')
    track('VMS Create Poi', { venueId, poiData });
}

export function recordHoverDownloadPoiButton(venueId) {
    track('VMS Hover Download Poi Button', { venueId });
}

export function recordDownloadPois(venueId) {
    track('VMS Download Pois', { venueId });
}

// ---------------------------------------------------------------------------
// Map note recording
// ---------------------------------------------------------------------------

export function recordSaveMapNote(venueId, noteId) {
    track('VMS Save Map Note', { venueId, noteId });
}

export function recordMapNoteClick(venueId, noteId) {
    track('VMS Click Map Note', { venueId, noteId });
}

export function recordAddMapNote(venueId) {
    track('VMS Add Map Note', { venueId });
}

export function recordMapNoteLoadFailure(venueId) {
    track('VMS Map Note Load Failed', { venueId });
}

// ---------------------------------------------------------------------------
// Venue / navigation recording
// ---------------------------------------------------------------------------

export function recordVenue(venueId, query) {
    track('VMS Select Venue', { venueId, query });
}

export function recordLevelChange(venueId, levelId) {
    track('VMS Change Level', { venueId, levelId });
}

// ---------------------------------------------------------------------------
// UI interaction recording
// ---------------------------------------------------------------------------

export function recordAddDropdown(venueId) {
    track('VMS Open Add Dropdown', { venueId });
}

export function recordMenuClick() {
    track('VMS Open Menu', {});
}

export function recordPublish(venueId) {
    track('VMS Publish Changes', { venueId });
}

// ---------------------------------------------------------------------------
// Auth recording
// ---------------------------------------------------------------------------

export function recordResetPassword() {
    track('VMS Reset Password', {});
}

export function recordLogout(userId) {
    track('VMS Logout', { userId });
}

// ---------------------------------------------------------------------------
// Error page recording
// ---------------------------------------------------------------------------

export function recordReloadVenuesButtonClick(userId) {
    track('VMS Click Reload Venues', { userId });
}

export function recordEmailSupportButtonClick(userId) {
    track('VMS Click Email Support', { userId });
}
